import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, CalendarClock, CloudRain, Wrench, Truck, AlertOctagon, Users } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";

const ICON_BY_KEY = {
  weather_delay: CloudRain,
  customer_request: Users,
  crew_conflict: Truck,
  equipment_issue: Wrench,
  no_access: AlertOctagon,
  other: CalendarClock,
};

export default function RescheduleDialog({ open, onOpenChange, booking, onRescheduled }) {
  const [reasons, setReasons] = useState([]);
  const [reasonKey, setReasonKey] = useState("weather_delay");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreferredDate(booking?.preferred_date || "");
    setPreferredTime(booking?.preferred_time || "");
    setNote("");
    api.get("/admin/reschedule-reasons").then(({ data }) => setReasons(data.reasons || []))
      .catch(() => setReasons([
        { key: "weather_delay", label: "Weather Delay" },
        { key: "customer_request", label: "Customer Request" },
        { key: "crew_conflict", label: "Crew Conflict" },
        { key: "equipment_issue", label: "Equipment Issue" },
        { key: "no_access", label: "Couldn't Access Property" },
        { key: "other", label: "Other" },
      ]));
  }, [open, booking]);

  const submit = async () => {
    if (!preferredDate) return toast.error("Pick a new date");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.booking_id}/reschedule`, {
        preferred_date: preferredDate,
        preferred_time: preferredTime || null,
        reason_key: reasonKey,
        note: note || null,
        notify_customer: true,
      });
      toast.success("Rescheduled — customer notified");
      onRescheduled && onRescheduled(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
    } finally { setSubmitting(false); }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule · {booking.service_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="text-xs text-gray-500">
            Currently <b>{booking.preferred_date} · {booking.preferred_time}</b>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600">Reason</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="reschedule-reason-picker">
              {reasons.map((r) => {
                const Icon = ICON_BY_KEY[r.key] || CalendarClock;
                const active = reasonKey === r.key;
                return (
                  <button key={r.key} type="button" onClick={() => setReasonKey(r.key)}
                    data-testid={`reschedule-reason-${r.key}`}
                    className={`rounded-xl border p-2.5 text-left flex items-center gap-2 ${active ? "bg-[#062818] text-white border-transparent" : "bg-white border-gray-200"}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-[12px] font-bold">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>New date</Label>
              <Input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} data-testid="reschedule-new-date" />
            </div>
            <div>
              <Label>New time</Label>
              <Input value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} placeholder="e.g. 9 AM" data-testid="reschedule-new-time" />
            </div>
          </div>
          <div>
            <Label>Internal note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any context for the crew" data-testid="reschedule-internal-note" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting} data-testid="reschedule-submit-btn" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reschedule & Notify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
